import {environment} from '@environments/environment';
import {hasValue} from '@shared/utils/helpers';

export class DateUtil {
  static diffInMs(date1: Date, date2: Date): number {
    return date1.getTime() - date2.getTime();
  }

  static nowUTC(): string {
    return new Date().toISOString();
  }

  static dateTimesNotEqual(date1: Date | string, date2: Date | string): boolean {
    return !DateUtil.dateTimesEqual(date1, date2);
  }

  static dateTimesEqual(date1: Date | string, date2: Date | string): boolean {
    if (typeof date1 !== 'string') {
      date1 = DateUtil.toUTC(date1);
    }

    if (typeof date2 !== 'string') {
      date2 = DateUtil.toUTC(date2);
    }

    const date1Utc = DateUtil.cleanMilliseconds(date1);
    const date2Utc = DateUtil.cleanMilliseconds(date2);

    return date1Utc === date2Utc;
  }

  static datesEqual(date1: Date | string, date2: Date | string): boolean {
    if (typeof date1 === 'string') {
      date1 = DateUtil.UTCStringToDate(date1);
    }

    if (typeof date2 === 'string') {
      date2 = DateUtil.UTCStringToDate(date2);
    }

    date1 = date1.toLocaleDateString();
    date2 = date2.toLocaleDateString();

    return date1 === date2;
  }

  static toUTC(date: Date | string): string {
    if (!hasValue(date)) {
      return null;
    }

    if (typeof date === 'string') {
      date = DateUtil.UTCStringToDate(date);
    }

    return date.toISOString();
  }

  static UTCStringToDate(utcString: any = null): Date {
    if (!hasValue(utcString)) {
      return null;
    }

    if (typeof utcString !== 'string') {
      if (utcString instanceof Date) {  // Returning the utcString if the passed parameter is a date variable (doesn't require parsing)
        return utcString;
      } else {
        if (!environment.production) {
          // eslint-disable-next-line max-len
          console.error('Function "UTCStringToDateTime" must be passed either a string that is UTC or a DateTime object (which will just be returned)');
        }
      }
    }

    if (utcString === null || utcString === '') {
      return null;
    }

    // Ensuring uppercase (this is mainly for when 'Z' && 'T' is lower case and is only precautionary)
    utcString = utcString.toUpperCase();

    // utcString = (!utcString.includes('Z')) ? `${utcString}Z` : utcString;
    utcString = DateUtil.normalizeUTC(utcString);

    return new Date(utcString);
  }

  static normalizeUTC(date: string): string {
    if (!hasValue(date)) {
      return null;
    }

    if (date.includes('+00:00')) {
      date = date.replace('+00:00', '');
    }

    return (!date.includes('Z')) ? `${date}Z` : date;
  }

  static toLocalTimeString(date: Date | string): string {
    if (!hasValue(date)) {
      return null;
    }

    if (typeof date === 'string') {
      date = DateUtil.UTCStringToDate(date);
    }

    return date.toLocaleTimeString()
  }

  private static cleanMilliseconds(date: string): string {
    const millisecondsMatch = '.000Z'

    if (date.includes(millisecondsMatch)) {
      date = date.replace(millisecondsMatch, 'Z');
    }

    return date;
  }
}

export function UTCStringToDateTime(dateString: any = null): Date {
  if (typeof dateString !== 'string') {
    if (dateString instanceof Date) {  // Returning the dateString if the passed parameter is a date variable (doesn't require parsing)
      return dateString;
    } else {
      if (!environment.production) {
        // eslint-disable-next-line max-len
        console.error('Function "UTCStringToDateTime" must be passed either a string that is UTC or a DateTime object (which will just be returned)');
      }
    }
  }

  if (dateString === null || dateString === '') {
    return null;
  }

  // Ensuring uppercase (this is mainly for when 'Z' && 'T' is lower case and is only precautionary)
  dateString = dateString.toUpperCase();

  dateString = (!dateString.includes('Z')) ? `${dateString}Z` : dateString;

  return new Date(dateString);
}


/**
 * Converts a string (yyyy-mm-ddTH:i:s || yyyy/mm/ddTH:i:s) -> yyyymmddTHis
 * @param date Date
 */
export function dateToIsoNoDelimiters(date: Date): string {
  let iso = date.toISOString();

  // Removing '/' delimiter for date separation
  if (iso.includes('/')) {
    iso = iso.split('/').join('');
  }

  // Removing '-' delimiter for date separation
  if (iso.includes('-')) {
    iso = iso.split('-').join('');
  }

  // Removing time delimiter ':'
  if (iso.includes(':')) {
    iso = iso.split(':').join('');
  }

  // Adding a 'Z' for ISO indication
  if (!iso.toUpperCase().includes('Z')) {
    iso = `${iso}Z`;
  }

  // Removing milliseconds from the ISO string
  if (iso.includes('.000')) {
    iso = iso.split('.000').join('');
  }

  return iso;
}

export function dateToLocaleDateString(date: Date): string {
  if (!hasValue(date)) {
    return null;
  }

  return date.toLocaleDateString();
}

export function dateToUTCString(date: Date): string {
  if (!hasValue(date)) {
    return null;
  }

  return date.toISOString();
}
